import { openPrint } from './StockAdmin'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateField } from './DatePicker'
import { API } from './api'
import { adminRequest } from './EshopCategories'

type VatRow = { rate: number; baseCents: number; vatCents: number; totalCents: number }
type Invoice = {
  id: string; number: string; kind: 'INVOICE' | 'CREDIT_NOTE'; issuedAt: string; supplyDate: string; paymentDate: string | null
  buyerName: string; companyIco: string | null; totalCents: number; vatSummary: VatRow[]; order?: { orderNumber: string } | null
}

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const day = (value: string | null) => value ? new Date(value).toLocaleDateString('sk-SK', { timeZone: 'Europe/Bratislava' }) : '—'
const isoDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bratislava' }).format(date)

export default function InvoicesAdmin() {
  const now = new Date()
  const [from, setFrom] = useState(isoDay(new Date(now.getFullYear(), now.getMonth(), 1)))
  const [to, setTo] = useState(isoDay(now))
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const query = `from=${from}&to=${to}`
  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const data = await adminRequest(`/api/admin/invoices?${query}`)
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Faktúry sa nepodarilo načítať.')
    } finally {
      setLoading(false)
    }
  }, [query])
  useEffect(() => { void load() }, [load])

  const summary = useMemo(() => {
    const rates = new Map<number, VatRow>()
    for (const invoice of invoices) for (const row of invoice.vatSummary) {
      const current = rates.get(row.rate) ?? { rate: row.rate, baseCents: 0, vatCents: 0, totalCents: 0 }
      rates.set(row.rate, { rate: row.rate, baseCents: current.baseCents + row.baseCents, vatCents: current.vatCents + row.vatCents, totalCents: current.totalCents + row.totalCents })
    }
    return [...rates.values()].sort((a, b) => b.rate - a.rate)
  }, [invoices])

  async function downloadCsv() {
    setError('')
    try {
      const response = await fetch(`${API}/api/admin/invoices/export.csv?${query}`, { credentials: 'include' })
      if (!response.ok) throw new Error(`Export sa nepodaril (HTTP ${response.status}).`)
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `faktury-eshop-${from}-${to}.csv`
      link.click()
      setTimeout(() => URL.revokeObjectURL(link.href), 1000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export sa nepodaril.')
    }
  }

  return <div className="panel invoices-admin">
    <div className="panel-heading"><div><h2>Faktúry e-shopu</h2><p>Faktúry k objednávkam zaplateným online a dobropisy pri vrátení peňazí.</p></div>
      <button className="text-btn" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button></div>
    <div className="invoice-filters">
      <DateField label="Od" value={from} onChange={setFrom} clearable={false} max={to} />
      <DateField label="Do" value={to} onChange={setTo} clearable={false} min={from} />
      <button className="primary-btn" onClick={() => void downloadCsv()} disabled={!invoices.length}>Export pre účtovníčku (CSV)</button>
      <span className="hint">Export pre Money S3 (XML) pripravíme podľa vzorového súboru z vášho Money S3.</span>
    </div>
    {error && <p className="error-message">{error}</p>}
    {summary.length > 0 && <div className="invoice-summary">{summary.map(row => <div key={row.rate}><span>DPH {row.rate} %</span><strong>{euro(row.totalCents)}</strong><small>základ {euro(row.baseCents)} · DPH {euro(row.vatCents)}</small></div>)}<div><span>Spolu</span><strong>{euro(invoices.reduce((s, i) => s + i.totalCents, 0))}</strong><small>{invoices.length} dokladov</small></div></div>}
    {loading ? <p className="empty-message">Načítavam faktúry…</p> : !invoices.length ? <p className="empty-message">V tomto období nie sú žiadne faktúry.</p> : <div className="table-wrap"><table>
      <thead><tr><th>ČÍSLO</th><th>DÁTUM</th><th>ODBERATEĽ</th><th>OBJEDNÁVKA</th><th>SUMA</th><th></th></tr></thead>
      <tbody>{invoices.map(invoice => <tr key={invoice.id}>
        <td><strong>{invoice.number}</strong>{invoice.kind === 'CREDIT_NOTE' && <small className="invoice-credit">dobropis</small>}</td>
        <td>{day(invoice.issuedAt)}</td>
        <td>{invoice.buyerName}{invoice.companyIco && <small> · IČO {invoice.companyIco}</small>}</td>
        <td>{invoice.order?.orderNumber ?? '—'}</td>
        <td className={invoice.totalCents < 0 ? 'negative' : ''}>{euro(invoice.totalCents)}</td>
        <td><button type="button" className="text-btn" onClick={() => openPrint(`/api/admin/invoices/${invoice.id}/html`, `${invoice.kind === 'CREDIT_NOTE' ? 'Dobropis' : 'Faktúra'} ${invoice.number}`)}>Zobraziť</button></td>
      </tr>)}</tbody>
    </table></div>}
  </div>
}
